/* globals grecaptcha */
import {airBlue, darkGray, landGreen, mediumGray} from '@modifi/colors'
import {Trans, useTranslation} from '@modifi/plugin-i18n'
import {canUseDOM} from '@modifi/client-ssr'
import {
  AttentionIcon,
  Col,
  InputContainer,
  InputPure,
  Row,
  VALIDATION_RULES,
  CheckboxPure,
} from '@modifi/ui'
import {Form, SubmitBehavior, FormInput} from '@modifi/plugin-forms'
import {createNonce} from '@modifi/utilities'
import PropTypes from 'prop-types'
import {propEq, reduce} from 'ramda'
import React, {Component} from 'react'

import {PartnersAreFriends} from '../../../../assets/graphics'
import {PRIVACY_POLICY_URL} from '../../../../constants/routes'
import {H3, P, Small} from '../../../../styles/typography'
import loadRecaptcha from '../../../../utils/loadRecaptcha'
import Link from '../../../common/link'
import ConnnectedSubmitButton from '../connected-submit-button'
import ExportVolumeCategory from '../export-volume-category-select'
import BrokerCheckBox from '../broker-checkbox'
import ProductTypeSelect from '../product-type-select'
import CountryCodeSelectInput from '../country-code-select-input'
import CountrySelectInput from '../country-select-input'
import {
  FormTile,
  PhoneInputWrapper,
  StyledAlert,
  StyledCnBadge,
  StyledFirstTradeFree,
  SuccessRow,
  SmallText,
} from './styles'
import {RequiredLabel} from '../../styles'

const InputWithContainer = props => <InputContainer inputComponent={InputPure} {...props} />

const getFieldConfigValues = reduce((m, {fieldName, value}) => ({...m, [fieldName]: value}), {})

export const fieldsShape = {
  productType: PropTypes.string,
  fullName: PropTypes.string,
  companyName: PropTypes.string,
  country: PropTypes.string,
  email: PropTypes.string,
  countryCode: PropTypes.string,
  phone: PropTypes.string,
}

const propTypes = {
  isLandingPageForm: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

const defaultProps = {
  isLandingPageForm: false,
}

const LandingColWrapper = ({isLandingPageForm, children}) => (
  <Col lg={isLandingPageForm ? 6 : 12}>{children}</Col>
)

LandingColWrapper.propTypes = propTypes
LandingColWrapper.defaultProps = defaultProps

class SignUp extends Component {
  static propTypes = {
    title: PropTypes.string,
    ctaText: PropTypes.string,
    isCountriesLoaded: PropTypes.bool.isRequired,
    countryOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ).isRequired,
    countryCodeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        flag: PropTypes.string,
        phonePrefix: PropTypes.string,
        countryName: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ).isRequired,
    isSubmitted: PropTypes.bool.isRequired,
    hasError: PropTypes.bool.isRequired,
    errorMessage: PropTypes.string.isRequired,
    fetchCountries: PropTypes.func.isRequired,
    submitForm: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    presetFields: PropTypes.shape(fieldsShape),
    defaultFields: PropTypes.shape(fieldsShape),
    firstTradeFree: PropTypes.bool,
    colorSchema: PropTypes.oneOf(['primary', 'secondary']),
    cnBadge: PropTypes.bool,
    leadSource: PropTypes.string,
    locale: PropTypes.string.isRequired,
    fieldConfig: PropTypes.arrayOf(
      PropTypes.shape({
        fieldName: PropTypes.string.isRequired,
        hidden: PropTypes.bool,
        value: PropTypes.string,
      })
    ),
    pkgFormsTrans: PropTypes.func.isRequired,
    isLandingPageForm: PropTypes.bool,
  }

  static defaultProps = {
    title: null,
    ctaText: 'signup.cta.send',
    presetFields: {},
    defaultFields: {},
    firstTradeFree: false,
    cnBadge: false,
    leadSource: 'inbound_website',
    fieldConfig: [],
    colorSchema: 'primary',
    isLandingPageForm: false,
  }

  constructor(props) {
    super(props)

    const defaultValues = getFieldConfigValues(props.fieldConfig)
    this.state = {
      defaultFormValues: {
        productType: defaultValues?.productType || null,
        fullName: defaultValues?.fullName || '',
        companyName: defaultValues?.companyName || '',
        country: defaultValues?.country || null,
        email: defaultValues?.email || '',
        countryCode: defaultValues?.countryCode || null,
        phone: defaultValues?.phone || '',
        exportVolumeCategory: defaultValues?.exportVolumeCategory || null,
      },
      isBroker: defaultValues?.isBroker || false,
      marketingConsentGiven: defaultValues?.marketingConsentGiven || false,
      captchaToken: '',
      isCaptchaValid: false,
      countryNonce: createNonce(),
      countryCodeNonce: createNonce(),
      brokerNonce: createNonce(),
    }
  }

  async componentDidMount() {
    const {resetForm, fetchCountries} = this.props
    resetForm()
    fetchCountries()

    if (canUseDOM) {
      await loadRecaptcha()
      grecaptcha.ready(async () => {
        await this.refreshCaptchaToken()
      })
    }
  }

  componentDidUpdate(prevProps) {
    const {isCountriesLoaded} = this.props
    // re-validate country & countryCode inputs when
    // countries are loaded & defaults are present
    /* eslint-disable react/no-did-update-set-state */
    if (isCountriesLoaded !== prevProps.isCountriesLoaded && isCountriesLoaded) {
      this.setState(state => {
        const finalState = {...state}
        if (state.defaultFormValues?.country) {
          finalState.countryNonce = createNonce()
        }
        if (state.defaultFormValues?.countryCode) {
          finalState.countryCodeNonce = createNonce()
        }
        return finalState
      })
    }
    /* eslint-enable */
  }

  componentWillUnmount() {
    if (document) {
      // removing recaptcha-badge & recaptcha-scripts
      document.querySelector('.grecaptcha-badge')?.remove()
      document.querySelectorAll('script').forEach(script => {
        if (script?.src.includes('recaptcha')) {
          script?.remove()
        }
      })
    }
  }

  refreshCaptchaToken = async () => {
    if (canUseDOM) {
      const captchaToken = await grecaptcha.execute(process.env.REACT_APP_CAPTCHA_SITE_KEY, {
        action: 'homepage',
      })
      this.setState({captchaToken, isCaptchaValid: true})
      return captchaToken
    }
    return undefined
  }

  handleSubmit = async changedFormData => {
    const captchaToken = await this.refreshCaptchaToken()

    const {submitForm, leadSource, fieldConfig} = this.props
    const {isBroker, marketingConsentGiven} = this.state
    const valuesFromFieldConfig = fieldConfig
      .filter(propEq('hidden', true))
      .reduce((m, {fieldName, value}) => {
        m[fieldName] = value // eslint-disable-line no-param-reassign
        return m
      }, {})

    const formData = {
      ...changedFormData,
      isBroker,
      marketingConsentGiven,
      captchaToken,
      channel: leadSource,
      ...valuesFromFieldConfig,
    }

    try {
      await submitForm(formData)

      // GTM event
      const dataLayer = window?.dataLayer ?? []
      dataLayer.push({
        event: 'signup_form_submitted',
      })

      window && window.scrollTo({top: 0, behavior: 'smooth'})
    } catch (e) {
      // do nothing
    }
  }

  render() {
    const {
      title,
      countryOptions,
      countryCodeOptions,
      isSubmitted,
      hasError,
      errorMessage,
      t,
      ctaText,
      firstTradeFree,
      cnBadge,
      locale,
      fieldConfig,
      colorSchema,
      pkgFormsTrans,
      isLandingPageForm,
    } = this.props

    const {
      isBroker,
      marketingConsentGiven,
      captchaToken,
      isCaptchaValid,
      brokerNonce,
      defaultFormValues,
    } = this.state

    // pre process fieldConfig for easier access
    const fields = fieldConfig.reduce((m, {fieldName, ...config}) => {
      m[fieldName] = config // eslint-disable-line no-param-reassign
      return m
    }, {})

    return (
      <FormTile>
        {firstTradeFree && (
          <StyledFirstTradeFree
            backgroundColor={colorSchema === 'secondary' ? airBlue : landGreen}
          />
        )}
        {cnBadge && <StyledCnBadge />}
        {isSubmitted ? (
          <SuccessRow alignItems="start" justifyContent="center">
            <Col lg={10}>
              <H3>{t('signup.success.title')}</H3>
              <P color={mediumGray}>{t('signup.success.desc')}</P>
              <PartnersAreFriends height={150} width={150} data-ident="success-icon" />
            </Col>
          </SuccessRow>
        ) : (
          <Form
            onSubmit={this.handleSubmit}
            emptyFormIsValid={false}
            data-type="form"
            data-ident="signup-form"
            submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID}
            defaultValues={defaultFormValues}
          >
            {title && (
              <H3 as="h1" mt={10}>
                {title}
              </H3>
            )}
            <Row>
              {fields?.productType?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <ProductTypeSelect locale={locale} />
                </LandingColWrapper>
              )}

              {fields?.fullName?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <FormInput
                    name="fullName"
                    inputComponent={InputWithContainer}
                    t={pkgFormsTrans}
                    label={<RequiredLabel>{t('signup.full-name.label')}</RequiredLabel>}
                    placeholder={t('signup.full-name.placeholder')}
                    {...VALIDATION_RULES.FULL_NAME_RULE}
                  />
                </LandingColWrapper>
              )}

              {fields?.companyName?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <FormInput
                    name="companyName"
                    inputComponent={InputWithContainer}
                    t={pkgFormsTrans}
                    label={<RequiredLabel>{t('signup.company-name.label')}</RequiredLabel>}
                    placeholder={t('signup.company-name.placeholder')}
                    {...VALIDATION_RULES.COMPANY_NAME_RULE}
                  />
                </LandingColWrapper>
              )}

              {fields?.exportVolumeCategory?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <ExportVolumeCategory brokerNonce={brokerNonce} isBroker={isBroker} />
                </LandingColWrapper>
              )}
              {fields?.country?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <CountrySelectInput countryOptions={countryOptions} janusTrans={t} />
                </LandingColWrapper>
              )}
              {fields?.email?.hidden !== true && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <FormInput
                    name="email"
                    inputComponent={InputWithContainer}
                    t={pkgFormsTrans}
                    type="email"
                    label={<RequiredLabel>{t('signup.email.label')}</RequiredLabel>}
                    placeholder={t('signup.email.placeholder')}
                    noBlur
                    {...VALIDATION_RULES.EMAIL_RULE}
                  />
                </LandingColWrapper>
              )}

              {(fields?.countryCode?.hidden !== true || fields?.phone?.hidden !== true) && (
                <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                  <Row>
                    {fields?.countryCode?.hidden !== true && (
                      <Col sm={4} md={5}>
                        <PhoneInputWrapper>
                          <CountryCodeSelectInput
                            countryCodeOptions={countryCodeOptions}
                            janusTrans={t}
                          />
                        </PhoneInputWrapper>
                      </Col>
                    )}
                    {fields?.phone?.hidden !== true && (
                      <Col
                        sm={fields?.countryCode?.hidden !== true ? 8 : 12}
                        md={fields?.countryCode?.hidden !== true ? 7 : 12}
                      >
                        <PhoneInputWrapper>
                          <FormInput
                            name="phone"
                            t={pkgFormsTrans}
                            inputComponent={InputWithContainer}
                            type="tel"
                            label={<RequiredLabel>{t('signup.phone.label')}</RequiredLabel>}
                            placeholder={t('signup.phone.placeholder')}
                            {...VALIDATION_RULES.PHONE_NUMBER_RULE}
                            minLengthMessage={({minLength, maxLength}) => [
                              'phone-number.error.min-length',
                              {minLength, maxLength},
                            ]}
                            maxLengthMessage={({minLength, maxLength}) => [
                              'phone-number.error.max-length',
                              {minLength, maxLength},
                            ]}
                          />
                        </PhoneInputWrapper>
                      </Col>
                    )}
                  </Row>
                </LandingColWrapper>
              )}

              <Col>
                <Row>
                  <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                    {fields?.isBroker?.hidden !== true && (
                      <Row>
                        <Col col={12} mb={30}>
                          <BrokerCheckBox
                            isBroker={isBroker}
                            onChange={() => {
                              this.setState({
                                isBroker: !isBroker,
                                brokerNonce: createNonce(),
                              })
                            }}
                          />
                        </Col>
                      </Row>
                    )}
                    {fields?.marketingConsentGiven?.hidden !== true && (
                      <Row>
                        <Col col={12} mb={30}>
                          <CheckboxPure
                            name="marketingConsentGiven"
                            label={
                              <Small>
                                <Trans
                                  i18nKey="signup.marketing-consent.label"
                                  t={t}
                                  components={[
                                    <Link
                                      type="anchor"
                                      to={PRIVACY_POLICY_URL}
                                      targetBlank
                                      color={darkGray}
                                      underline
                                    >
                                      {t('privacy-statement')}
                                    </Link>,
                                  ]}
                                />
                              </Small>
                            }
                            checked={marketingConsentGiven || false}
                            onChange={() => {
                              this.setState({
                                marketingConsentGiven: !marketingConsentGiven,
                              })
                            }}
                            checkboxPosition="top"
                          />
                        </Col>
                      </Row>
                    )}
                  </LandingColWrapper>
                  <LandingColWrapper isLandingPageForm={isLandingPageForm}>
                    <input type="hidden" name="recaptcha" value={captchaToken} />
                    {hasError && (
                      <StyledAlert>
                        <AttentionIcon height={24} width={24} />
                        <span>{errorMessage}</span>
                      </StyledAlert>
                    )}
                    <ConnnectedSubmitButton
                      colorSchema={colorSchema}
                      isCaptchaValid={isCaptchaValid}
                      ctaText={ctaText}
                    />
                    <SmallText>{t('signup.mandatory-fields.note')}</SmallText>
                    <Small>
                      <Trans
                        i18nKey="signup.privacy-disclaimer"
                        t={t}
                        components={[
                          <Link
                            type="anchor"
                            to={PRIVACY_POLICY_URL}
                            targetBlank
                            color={darkGray}
                            underline
                          >
                            {t('privacy-statement')}
                          </Link>,
                        ]}
                      />
                    </Small>
                  </LandingColWrapper>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </FormTile>
    )
  }
}

const SignUpWrapper = props => {
  const {t} = useTranslation('pkg:forms')
  return <SignUp {...props} pkgFormsTrans={t} />
}

export default SignUpWrapper
